import { useCallback, useEffect, useRef } from 'react';

import { toFailureReason } from '../failureReason';
import { VideoGenerationSubmitAction, VideoGenerationSubmitState, VideoGenerationPollResponse } from '../types';
import { PollResult, useGenerationPolling } from './useGenerationPolling';

export type UseVideoGenerationPollingProps = {
    videoGenerationSubmitState: VideoGenerationSubmitState;
    dispatchVideoGenerationSubmit: (action: VideoGenerationSubmitAction) => void;
    refreshCredits: () => Promise<number>;
};

export const useVideoGenerationPolling = ({
    videoGenerationSubmitState,
    dispatchVideoGenerationSubmit,
    refreshCredits,
}: UseVideoGenerationPollingProps): void => {
    const videoSubmitStateRef = useRef<VideoGenerationSubmitState>(videoGenerationSubmitState);
    useEffect(() => {
        videoSubmitStateRef.current = videoGenerationSubmitState;
    });

    const handleVideoGenerationPoll = useCallback(
        async (taskId: number): Promise<PollResult> => {
            const res = await fetch(`/api/video-generation/${taskId}`);
            const payload = (await res.json()) as VideoGenerationPollResponse;

            if (payload.status === 'SUCCEEDED' && payload.videoUrl) {
                const { aiContentId, lastAiContentId, aiContentType, aiModelName } = videoSubmitStateRef.current;
                dispatchVideoGenerationSubmit({
                    type: 'GENERATION_SUCCESS',
                    aiContentId,
                    lastAiContentId,
                    aiContentType,
                    aiModelName,
                    videoUrl: payload.videoUrl,
                });
                return 'success';
            }

            if (payload.status === 'FAILED' || payload.status === 'CANCELLED') {
                void refreshCredits();
                dispatchVideoGenerationSubmit({
                    type: 'GENERATION_ERROR',
                    failureReason: toFailureReason(payload.failureReason) ?? 'generation_failed',
                });
                return 'error';
            }

            return 'continue';
        },
        [dispatchVideoGenerationSubmit, refreshCredits],
    );

    useGenerationPolling({
        taskId: videoGenerationSubmitState.pendingTaskId,
        status: videoGenerationSubmitState.status,
        onPoll: handleVideoGenerationPoll,
        onTimeout: () => {
            void refreshCredits();
            dispatchVideoGenerationSubmit({ type: 'GENERATION_ERROR' });
        },
    });
};
